/**
 * Auth State Synchronization
 *
 * Syncs authentication state between:
 * - Web app (full browser)
 * - Extension sidebar (iframe)
 * - Extension popup
 * - Extension background
 */

import { createLogger } from '@ctrl-enter/tools/utils';
import type { User } from '@ctrl-enter/types';

const logger = createLogger('web:auth:sync');

export type ChatAccessRequirement = 'requires_agreement' | 'requires_payment';

export type ChatAccessInfo = {
  hasAccess: boolean;
  requirements: ChatAccessRequirement[];
  subscriptionStatus: 'active' | 'inactive' | 'pending' | 'processing' | 'unknown';
};

type AuthStateMessage = {
  type: 'AUTH_STATE_CHANGED';
  isAuthenticated: boolean;
  user: User | null;
  chatAccess?: ChatAccessInfo; // Optional chat access information
};

/**
 * Broadcast auth state change to extension
 */
export function broadcastAuthState(
  isAuthenticated: boolean,
  user: User | null,
  chatAccess?: ChatAccessInfo
) {
  const message: AuthStateMessage = {
    type: 'AUTH_STATE_CHANGED',
    isAuthenticated,
    user,
    chatAccess,
  };

  // If we're in an iframe (extension sidebar), send to parent
  if (window.parent !== window) {
    logger.info('📤 [AUTH-SYNC] Sending auth state to parent window (extension)');
    window.parent.postMessage(message, '*');
  }

  // If we have access to chrome.storage (extension context), update it
  if (typeof chrome !== 'undefined' && chrome.storage) {
    logger.info('📤 [AUTH-SYNC] Updating chrome.storage with auth state');
    chrome.storage.local
      .set({
        authState: {
          isAuthenticated,
          user,
          timestamp: Date.now(),
        },
      })
      .catch(err => {
        logger.error('❌ [AUTH-SYNC] Failed to update chrome.storage:', err);
      });
  }

  // Sync device ID across contexts using postMessage (works across iframe boundaries)
  try {
    const deviceId = localStorage.getItem('ctrl-enter-device-id');
    if (deviceId) {
      logger.info('📤 [AUTH-SYNC] Broadcasting device ID to ensure consistency');

      // Send to parent if we're in iframe
      if (window.parent !== window) {
        logger.info('📤 [AUTH-SYNC] Sending device ID to parent window');
        window.parent.postMessage(
          {
            type: 'DEVICE_ID_SYNC',
            deviceId,
          },
          '*'
        );
      }

      // Also dispatch local event for same-window listeners
      window.dispatchEvent(
        new CustomEvent('device-id-sync', {
          detail: { deviceId },
        })
      );
    }
  } catch (error) {
    logger.warn('⚠️ [AUTH-SYNC] Failed to sync device ID:', error);
  }

  // Dispatch custom event for same-window listeners
  window.dispatchEvent(
    new CustomEvent('auth-state-changed', {
      detail: { isAuthenticated, user },
    })
  );

  logger.info('✅ [AUTH-SYNC] Auth state broadcasted', {
    isAuthenticated,
    userEmail: user?.email,
    hasAccess: chatAccess?.hasAccess,
    requirements: chatAccess?.requirements,
  });
}

/**
 * Listen for auth state changes from extension
 */
export function listenForAuthStateChanges(
  callback: (isAuthenticated: boolean, user: User | null, chatAccess?: ChatAccessInfo) => void
) {
  // Listen for postMessage from parent (extension)
  const messageHandler = (event: MessageEvent) => {
    if (event.data?.type === 'AUTH_STATE_CHANGED') {
      logger.info('📥 [AUTH-SYNC] Received auth state from parent:', event.data);
      callback(event.data.isAuthenticated, event.data.user, event.data.chatAccess);
    }

    // Listen for device ID sync from parent/iframe
    if (event.data?.type === 'DEVICE_ID_SYNC') {
      const { deviceId } = event.data;
      if (deviceId) {
        logger.info('📥 [AUTH-SYNC] Received device ID from other context:', deviceId);
        try {
          const currentDeviceId = localStorage.getItem('ctrl-enter-device-id');
          if (currentDeviceId !== deviceId) {
            logger.info('🔄 [AUTH-SYNC] Updating device ID to match other context');
            localStorage.setItem('ctrl-enter-device-id', deviceId);
            // Clear cached device ID in client.ts
            window.dispatchEvent(new CustomEvent('device-id-sync', { detail: { deviceId } }));
          }
        } catch (error) {
          logger.error('❌ [AUTH-SYNC] Failed to sync device ID:', error);
        }
      }
    }

    // Parent: Handle device ID request from iframe
    if (event.data?.type === 'REQUEST_DEVICE_ID') {
      logger.info('📥 [AUTH-SYNC] Received device ID request from iframe');
      try {
        const deviceId = localStorage.getItem('ctrl-enter-device-id');
        if (deviceId && event.source) {
          logger.info('📤 [AUTH-SYNC] Sending device ID to iframe:', deviceId);
          (event.source as Window).postMessage(
            {
              type: 'DEVICE_ID_SYNC',
              deviceId,
            },
            '*'
          );
        }
      } catch (error) {
        logger.error('❌ [AUTH-SYNC] Failed to send device ID to iframe:', error);
      }
    }
  };

  window.addEventListener('message', messageHandler);

  // Listen for chrome.storage changes (extension context)
  if (typeof chrome !== 'undefined' && chrome.storage) {
    chrome.storage.onChanged.addListener((changes, areaName) => {
      if (areaName === 'local' && changes.authState) {
        const newState = changes.authState.newValue;
        if (newState) {
          logger.info('📥 [AUTH-SYNC] Received auth state from chrome.storage:', newState);
          callback(newState.isAuthenticated, newState.user, newState.chatAccess);
        }
      }
    });
  }

  // Listen for localStorage changes (cross-tab sync)
  const storageHandler = (event: StorageEvent) => {
    logger.info('📥 [AUTH-SYNC] Storage event received:', {
      key: event.key,
      newValue: event.newValue ? 'present' : 'null',
      url: event.url,
    });

    if (event.key === 'ctrl-enter-tokens' || event.key === null) {
      // Tokens changed - trigger auth state refresh
      logger.info('📥 [AUTH-SYNC] Auth tokens changed in another tab, triggering re-check');

      // Check if tokens exist now
      const tokensStr = localStorage.getItem('ctrl-enter-tokens');
      if (tokensStr) {
        logger.info('📥 [AUTH-SYNC] Found new tokens, will trigger re-auth');
      }

      // Signal to auth provider to re-check auth state
      window.dispatchEvent(new CustomEvent('auth-tokens-changed'));
    }
  };

  window.addEventListener('storage', storageHandler);

  // Listen for extension auth sync (from content script)
  const extensionSyncHandler = (event: Event) => {
    const customEvent = event as CustomEvent;
    if (customEvent.detail) {
      logger.info('📥 [AUTH-SYNC] Extension auth sync received:', customEvent.detail);
      callback(
        customEvent.detail.isAuthenticated,
        customEvent.detail.user,
        customEvent.detail.chatAccess
      );
    }
  };

  window.addEventListener('extension-auth-sync', extensionSyncHandler);

  // Return cleanup function
  return () => {
    window.removeEventListener('message', messageHandler);
    window.removeEventListener('storage', storageHandler);
    window.removeEventListener('extension-auth-sync', extensionSyncHandler);
  };
}
